import React, { useRef, useCallback, useState, useEffect } from 'react';
import classNames from 'classnames/bind';
import styles from './Blog.module.scss';
import { CiSearch } from 'react-icons/ci';
import { FaPlay } from 'react-icons/fa';
import moment from 'moment';

const cx = classNames.bind(styles);

const getHardwareClass = (hardware) => {
  if (!hardware) return 'tag_red'; // Default to red if undefined/null (or follow '빔프로젝터' style)
  const hw = hardware.trim();
  // "LED + 빔프로젝트" or similar combinations
  if (hw.includes('LED') && (hw.includes('빔') || hw.includes('프로젝터'))) {
    return 'tag_purple';
  }
  if (hw.includes('LED')) {
    return 'tag_cyan';
  }
  if (hw.includes('빔') || hw.includes('프로젝터')) {
    return 'tag_red';
  }
  return 'tag_red'; // Default for other values
};

export default function Blog({
  data,
  search,
  categories,
  handleSearch,
  handleCategory,
  handleDetailMove,
  handleLoadMore,
  hasMore,
}) {
  const [preview, setPreview] = useState(null); // { type: 'image'|'video', url: string, poster?: string }
  const observer = useRef();

  const lastElementRef = useCallback(
    (node) => {
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          handleLoadMore();
        }
      });
      if (node) observer.current.observe(node);
    },
    [handleLoadMore, hasMore],
  );

  const handlePreview = (e, item) => {
    e.stopPropagation();
    if (item.thumbnailVideoUrl) {
      setPreview({
        type: 'video',
        url: item.thumbnailVideoUrl,
        poster: item.thumbnailImageUrl,
      });
    } else {
      setPreview({
        type: 'image',
        url: item.thumbnailImageUrl,
      });
    }
  };

  const closePreview = useCallback(() => {
    setPreview(null);
  }, []);

  // Handle ESC key to close preview
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && preview) {
        closePreview();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [preview, closePreview]);

  return (
    <div className={cx('container')}>
      <div className={cx('inner')}>
        <div className={cx('wrapper')}>
          <div className={cx('title_box')}>
            <h2 className={cx('title')}>블로그</h2>
            <div className={cx('filter_box')}>
              <select
                className={cx('category_select')}
                onChange={handleCategory}
              >
                <option value="">전체</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>
                    {category.name}
                  </option>
                ))}
              </select>
              <div className={cx('search_box')}>
                <input
                  className={cx('search_input')}
                  placeholder="제목을 입력해 주세요."
                  value={search}
                  onChange={handleSearch}
                />
                <CiSearch className={cx('search_icon')} />
              </div>
            </div>
          </div>

          <div className={cx('grid_box')}>
            {data.map((item, index) => {
              const isLast = index === data.length - 1;
              return (
                <div
                  key={item.id}
                  className={cx('card')}
                  ref={isLast ? lastElementRef : null}
                  onClick={() => handleDetailMove(item.id, item.title)}
                >
                  <div
                    className={cx('thumbnail_box')}
                    onClick={(e) => handlePreview(e, item)}
                  >
                    {item.thumbnailVideoUrl ? (
                      <>
                        <div className={cx('play_icon_overlay')}>
                          <FaPlay />
                        </div>
                        {/* 썸네일 이미지가 없거나 로드 실패할 경우를 대비하여 비디오 태그를 사용하여 포스터를 보여주거나, 이미지가 있으면 이미지 표시 */}
                        {item.thumbnailImageUrl ? (
                          <img
                            className={cx('image')}
                            src={item.thumbnailImageUrl}
                            alt={item.title}
                          />
                        ) : (
                          <video
                            className={cx('video_thumbnail')}
                            src={item.thumbnailVideoUrl}
                            muted
                            preload="metadata"
                            aria-label={`${item.title} 미리보기 영상`}
                          />
                        )}
                      </>
                    ) : (
                      <img
                        className={cx('image')}
                        src={item.thumbnailImageUrl}
                        alt={item.title}
                      />
                    )}
                  </div>
                  <div className={cx('info_box')}>
                    <div className={cx('text_top')}>
                      <h3 className={cx('card_title')}>{item.title}</h3>
                      <p className={cx('card_date')}>
                        {moment(item.createdAt).format('YYYY. MM. DD')}
                      </p>
                    </div>
                    <div className={cx('tags')}>
                      {/* {item.hardware ? (
                        <span
                          className={cx('tag', getHardwareClass(item.hardware))}
                        >
                          {item.hardware}
                        </span>
                      ) : (
                        <>
                          {item.hardware && (
                            <span
                              className={cx(
                                'tag',
                                getHardwareClass(item.hardware),
                              )}
                            >
                              {item.hardware}
                            </span>
                          )}
                        </>
                      )} */}

                      {item.hardware && (
                        <span
                          className={cx('tag', getHardwareClass(item.hardware))}
                        >
                          {item.hardware}
                        </span>
                      )}

                      {item.industry && (
                        <span className={cx('tag', 'tag_gray')}>
                          {item.industry}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {data.length === 0 && (
            <div className={cx('empty_box')}>
              <span className={cx('empty_text')}>데이터가 없습니다.</span>
            </div>
          )}
        </div>
      </div>

      {preview && (
        <div className={cx('modal_overlay')} onClick={closePreview}>
          <div
            className={cx('modal_content')}
            onClick={(e) => e.stopPropagation()}
          >
            <button className={cx('modal_close')} onClick={closePreview}>
              &times;
            </button>
            {preview.type === 'video' ? (
              <video
                src={preview.url}
                poster={preview.poster}
                controls
                autoPlay
                className={cx('modal_media')}
              />
            ) : (
              <img
                src={preview.url}
                alt="Preview"
                className={cx('modal_media')}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
